import { TamediaStyles } from '@ta-interaktiv/microfrontend-template-helpers'
import styled from 'styled-components'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { useEffect, useState } from 'react'
import GlobalContext from 'Contexts/GlobalContext'
import { de, fr } from 'Globals/Locale'

import { Search } from 'components/Search'
import { formatNumber, percentOfPopulation } from 'Utils'
import { MapWrapper } from 'components/MapWrapper'
import { AllNamesTable } from 'components/AllNamesTable'
import { LoadAllNames } from 'components/LoadAllNames'
import { useEventListener } from 'usehooks-ts'
import { LoadVerzeichnis } from 'components/LoadVerzeichnis'
import { LoadGDETop3 } from '../LoadGDETop3'
import smoothscroll from 'smoothscroll-polyfill'
import { DynamicText } from 'components/DynamicText'
import { Fragment } from 'react'
// kick off the polyfill!
smoothscroll.polyfill()
window.nachnamen = window.nachnamen || {}

const debug = window.document.title === 'TA-Interaktiv Microfrontend'
export function prepNameList(allNamesData: any) {
  const all: {
    id: string | number
    name: string
    [key: string]: any
  }[] = []
  allNamesData.map(
    (
      { name, count, rank }: { name: string; count: number; rank: number },
      i: number
    ) => {
      var names = name.split(',')
      names.forEach((n: string) => {
        all.push({
          id: i,
          name: n,
          count,
          rank,
        })
      })
    }
  )

  return all
}

const MainComponent = ({
  tenant,
  colorMode,
  properties,
}: MicroFrontendProps) => {
  const [currentName, setCurrentName] = useState<CurrentName>({
    abs: 37,
    id: 's57',
    incollection: true,
    name: 'Orlik',
    rel: 0.001,
  })

  var part = properties?.part || '1'

  const [allNamesData, setAllNamesData] = useState<any>(
    window.nachnamen.allnames ? window.nachnamen.allnames : undefined
  )
  const [nameList, setNameList] = useState<CurrentName[]>()
  const [activeMunicipalityId, setActiveMunicipalityId] = useState<number>()
  const [foundMunicipalityFromUrl, setFoundMunicipalityFromUrl] =
    useState(false)
  const [currentSort, setCurrentSort] = useState('rel')
  const currentLocale = tenant?.lang === 'fr' ? fr : de
  const [selectedNameData, setSelectedNameData] = useState<CurrentName>()

  function findCloseNames(namedata: CurrentName, max: number) {
    if (!nameList) return []
    var names = nameList
      .filter((f) => {
        return (
          f.name !== namedata.name &&
          f.count > namedata.count * 0.8 &&
          f.count < namedata.count * 1.2
        )
      })
      .sort((a, b) => 0.5 - Math.random())
    if (names.length > max) names = names.slice(0, max)
    return names
  }
  const [closestNames, setClosestNames] = useState<CurrentName[]>(
    window.nachnamen.closestNames ? window.nachnamen.closestNames : []
  )

  useEventListener('name_changed' as any, () => {
    setSelectedNameData(window.nachnamen.selectedNameData)
    setClosestNames(window.nachnamen.closestNames)
  })
  useEventListener('allnames_loaded' as any, () => {
    setAllNamesData(window.nachnamen.allnames)
    setNameList(
      prepNameList(window.nachnamen.allnames).sort((a, b) => a.rank - b.rank)
    )
  })

  useEffect(() => {
    // Get params from URL and set active municipality
    const searchParams = new URL(String(window.location)).searchParams
    const muniParam = searchParams.get(currentLocale.bfsIdParamName)
    const bfsIdFromUrl = muniParam && parseInt(muniParam)

    if (bfsIdFromUrl) {
      setFoundMunicipalityFromUrl(true)
      setActiveMunicipalityId(+bfsIdFromUrl)
    }

    const handleEvent = (event: CustomEvent) => {
      console.log('📫 GDE-Change received')
      const newBfsId = event.detail
      newBfsId && setActiveMunicipalityId(newBfsId)
    }
    window.addEventListener('changeGDE', handleEvent as EventListener)

    const event = new CustomEvent('name_changed')
    window.dispatchEvent(event)

    const buttonUp = document?.querySelector('[class*=BackToTop_root]')
    buttonUp?.setAttribute('style', 'opacity: 0')

    return () => {
      window.removeEventListener('changeGDE', handleEvent as EventListener)
    }
  }, [])

  function getSelectedData(name: string) {
    if (!nameList) return undefined
    let selectedName = nameList
      ? nameList.find((d: any) => d.name.toLowerCase() === name.toLowerCase())
      : undefined

    let closest = selectedName ? findCloseNames(selectedName, 3) : []

    setSelectedNameData(selectedName)
    window.nachnamen.selectedNameData = selectedName
    window.nachnamen.closestNames = closest
  }
  useEffect(() => {
    if (!nameList) return
    getSelectedData(currentName.name)
    const event = new CustomEvent('name_changed')
    window.dispatchEvent(event)
  }, [nameList])

  useEffect(() => {
    activeMunicipalityId &&
      foundMunicipalityFromUrl &&
      setFoundMunicipalityFromUrl(false)
  }, [activeMunicipalityId])

  useEffect(() => {
    window.nachnamen.currentName = currentName
    getSelectedData(currentName.name)
    const event = new CustomEvent('name_changed')
    window.dispatchEvent(event)
  }, [currentName])

  return (
    <AppContainer $colorMode={colorMode}>
      {(part === '1' || debug) && (
        <>
          <LoadAllNames />
          {nameList && <LoadVerzeichnis />}
          {nameList && <LoadGDETop3 />}
          {nameList ? (
            <TextWidth>
              <SearchLabel>{currentLocale.searchtitle}</SearchLabel>
              <Search
                data={nameList.map((d: any) => ({
                  name: d.name as string,
                }))}
                handleOnSelect={(e: any) => {
                  setActiveMunicipalityId(undefined)
                  setCurrentName(e)
                }}
                labels={{
                  noResults: currentLocale.namesearch.noResults,
                  placeholder: currentLocale.namesearch.placeholder,
                }}
              />
              <MapHint>{currentLocale.hint1}</MapHint>
            </TextWidth>
          ) : (
            <></>
          )}

          {selectedNameData && tenant.lang === 'de' && (
            <NameDisplay>
              <Name>{currentName.name}</Name>
              <NameSub>
                Dieser Nachname kommt in der Schweiz{' '}
                <b>{formatNumber(selectedNameData.count)}-mal </b> vor. Das
                reicht für <b>Rang {formatNumber(selectedNameData.rank)}</b> von
                über einer halben Million Nachnamen, die es insgesamt gibt.{' '}
                {closestNames.length === 0 ? (
                  <>Kein Name ist ähnlich häufig.</>
                ) : (
                  <>
                    {closestNames.length === 1 ? (
                      <>
                        Ähnlich häufig gibt es zum Beispiel den Namen{' '}
                        {closestNames[0].name}.
                      </>
                    ) : (
                      <>
                        Ähnlich häufig gibt es zum Beispiel die Namen{' '}
                        <b>
                          {closestNames.map((name, i) => (
                            <Fragment key={'dynamictext_name_' + i}>
                              {name.name}
                              {i === closestNames.length - 2
                                ? ' und '
                                : i < closestNames.length - 1
                                ? ', '
                                : ''}
                            </Fragment>
                          ))}
                        </b>
                        .
                      </>
                    )}
                  </>
                )}
              </NameSub>
            </NameDisplay>
          )}
          {selectedNameData && tenant.lang === 'fr' && (
            <NameDisplay>
              <Name>{currentName.name}</Name>
              <NameSub>
                Ce nom de famille apparaît{' '}
                <b>{formatNumber(selectedNameData.count)} fois </b> en Suisse.
                Cela correspond à{' '}
                <b>
                  {percentOfPopulation(selectedNameData.count).replace(
                    '<',
                    ' moins de'
                  )}
                </b>{' '}
                de la population résidante permanente.{' '}
                {closestNames.length === 0 ? (
                  <>Aucun nom n'est aussi fréquent.</>
                ) : (
                  <>
                    {closestNames.length === 1 ? (
                      <>
                        Le nom {closestNames[0].name}, par exemple, est tout
                        aussi fréquent.
                      </>
                    ) : (
                      <>
                        Les noms{' '}
                        <b>
                          {closestNames.map((name, i) => (
                            <Fragment key={'dynamictext_name_' + i}>
                              {name.name}
                              {i === closestNames.length - 2
                                ? ' et '
                                : i < closestNames.length - 1
                                ? ', '
                                : ''}
                            </Fragment>
                          ))}
                        </b>{' '}
                        par exemple, sont tout aussi fréquents.
                      </>
                    )}
                  </>
                )}
              </NameSub>
            </NameDisplay>
          )}
        </>
      )}
      {(part === '2' || debug) && (
        <GlobalContext.Provider
          value={{ tenant, colorMode, properties, locale: currentLocale }}
        >
          <MapBG>
            <MapWrapper tenant={tenant} handleSortChange={setCurrentSort} />
          </MapBG>
        </GlobalContext.Provider>
      )}
      {(part === '4' || debug) && <DynamicText locale={tenant?.lang} />}
      {(part === '3' || debug) && allNamesData && (
        <TextWidth>
          <AllNamesTable currentLocale={currentLocale} />
        </TextWidth>
      )}
    </AppContainer>
  )
}
//   const { locale } = useContext(GlobalContext) ?? { locale: de }

const MapHint = styled.div`
  text-align: center;
  max-width: 450px;
  font-size: 12px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4em;
  margin-bottom: 30px;
  color: var(--gray-3);
  @media screen and (max-width: 711px) {
    max-width: calc(100vw - 30px);
  }
`

const SearchLabel = styled.div`
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  font-family: var(--font-sans-serif);
  font-weight: 900;
  margin-bottom: 15px;
`
const NameDisplay = styled.div`
  pointer-events: none;
  background-color: var(--purple-main);
  background: var(--purple-main);
  background: linear-gradient(
    0deg,
    var(--purple-dark-1) 0%,
    var(--purple-main) 100%
  );
  color: #fff;
  margin-left: auto;
  margin-right: auto;
  padding: 30px;
  display: flex;
  padding-bottom: 60px;
  flex-direction: column;
  text-align: center;
  border-radius: 1rem;
  font-size: 14px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 15px;
  font-family: var(--font-sans-serif);
  min-width: 250px;
  max-width: 500px;
  animation: slide-in-top 0.3s ease-in-out;
  @media screen and (max-width: 711px) {
    min-width: unset;
    width: 100% !important;
    max-width: calc(100vw - 90px) !important;
  }
  @keyframes slide-in-top {
    0% {
      transform: translate3d(0, -50px, 0);
      opacity: 0;
      max-height: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
      max-height: 400px;
    }
  }
`
const Name = styled.div`
  font-family: var(--font-sans-serif);
  font-weight: 900;
  font-size: 32px;
  margin-bottom: 20px;
  margin-top: 30px;
  line-height: 1.4em;
`
const NameSub = styled.div`
  font-size: 18px;
`

const MapBG = styled.div`
  background-color: var(--map-background-color);
  max-width: calc(100vw - 0px);
  padding: 15px;
  width: 100%;
  padding-bottom: 30px;
  padding-top: 30px;
  margin-bottom: 60px;
  min-height: 300px;
  /* display: flex; */
  /* justify-content: center; */
  align-items: center;
`

const AppContainer = styled(TamediaStyles)`
  ${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  padding: 1em 0;
  touch-action: pan-x pan-y;
  --font-sans-serif: 'Libre Franklin', Helvetica, Arial, Verdana, sans-serif;
  --font-serif: 'Sole Serif', 'Times New Roman', serif;
  #search {
    font-size: 18px;
  }

  .municipality-search {
    .result {
      border-bottom: 1px solid var(--gray-5);

      &:last-child {
        border-bottom: none;
      }
      color: var(--text-color);
    }
    .results {
      box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);

      background-color: var(--site-background);
    }
    .resultMeta {
      display: none;
    }
    @media screen and (max-width: 711px) {
      width: 100%;
    }
  }
`
export default MainComponent
